import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { useAuthStore } from '@/features/auth/store/auth';
import {
  getAccessToken,
  hasAuthSession,
  isTokenExpired,
} from '@/shared/api/storage';

/**
 * BFF client. Sends the stored access token as `Authorization: Bearer`.
 */
const client: AxiosInstance = axios.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL ?? '',
});

/**
 * Auth endpoints that do not require a valid token.
 * These are called during the login / MFA flow, before any token exists,
 * so the expiration guard must be skipped for them.
 */
const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/fulfill-mfa',
  '/auth/leverage-identity-mfa',
  '/auth/reset-password',
  '/auth/identify',
  '/auth/federation/check',
];

const isPublicAuthRequest = (url?: string): boolean =>
  PUBLIC_AUTH_PATHS.some((path) => url?.startsWith(path));

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url as string | undefined;

    // Skip session reset for accept-invitation: a 401 here may indicate
    // a scope/company mismatch rather than an invalid session, so we
    // let the error propagate to the component's onError handler.
    const isAcceptInvitation = requestUrl?.startsWith(
      '/users/accept-invitation'
    );

    if (
      status === 401 &&
      !isPublicAuthRequest(requestUrl) &&
      !isAcceptInvitation
    ) {
      const { reset } = useAuthStore.getState();
      reset();
    }

    return Promise.reject(error);
  }
);

/**
 * Request interceptor
 *
 * Pre-empts requests once the stored session has expired and attaches the
 * access token, unless the caller already set `Authorization` (e.g. the
 * onboarding password step or the post-login platform-access check).
 */
client.interceptors.request.use((config) => {
  if (config.headers.has('Authorization')) return config;

  // Skip token expiration check for pre-auth endpoints (login, MFA, password reset)
  if (
    !isPublicAuthRequest(config.url) &&
    hasAuthSession() &&
    isTokenExpired()
  ) {
    const { reset } = useAuthStore.getState();
    reset();
    return Promise.reject(new Error('Token expired'));
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

export default client;
